import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { Td } from '@/components/Table';
import {
  OptimisationTestUnitResource,
  OptimisationTestUnitStatsResource,
} from '@/types/resources';
import { durationFormatter } from '@/utils/helpers';
import { RootState } from '@/utils/store';

const Tr = styled.tr`
  > td {
    padding-top: 16px;
    padding-bottom: 16px;
    vertical-align: top;
    border-top: 1px solid ${({ theme }) => theme.colors.lightGray};
  }
`;

const getAvg = <T extends object>(array: Array<T>, key: keyof T) =>
  array
    .filter((a) => !!a[key])
    .reduce((a, b) => a + parseFloat(b[key] as unknown as string), 0) /
  array.length;

interface Props {
  tests: OptimisationTestUnitResource[];
}

const AvgTipStatsRow: FC<Props> = ({ tests }) => {
  const tableViewOptions = useSelector(
    (rootState: RootState) =>
      rootState.options.tableViews['optimisationTipsTable'],
  );

  const avgMetrics = useMemo(() => {
    const stats = tests
      .filter((t) => !!t.searchStats)
      .map((t) => t.searchStats) as OptimisationTestUnitStatsResource[];

    const props = tests.map((t) => t.properties);
    return {
      sceneDuration: getAvg<OptimisationTestUnitResource['properties']>(
        props,
        'sceneDuration',
      ),
      views: getAvg<OptimisationTestUnitStatsResource>(stats, 'views'),
      ctrPercentage: getAvg<OptimisationTestUnitStatsResource>(
        stats,
        'ctrPercentage',
      ),
      avgWatchtime: getAvg<OptimisationTestUnitStatsResource>(
        stats,
        'avgWatchtime',
      ),
      '30sViewsPercentage': getAvg<OptimisationTestUnitStatsResource>(
        stats,
        '30sViewsPercentage',
      ),
      e30s: getAvg<OptimisationTestUnitStatsResource>(stats, 'e30s'),
      e45s: getAvg<OptimisationTestUnitStatsResource>(stats, 'e45s'),
      e60s: getAvg<OptimisationTestUnitStatsResource>(stats, 'e60s'),
      e80s: getAvg<OptimisationTestUnitStatsResource>(stats, 'e80s'),
      e120s: getAvg<OptimisationTestUnitStatsResource>(stats, 'e120s'),
      e150s: getAvg<OptimisationTestUnitStatsResource>(stats, 'e150s'),
      e240s: getAvg<OptimisationTestUnitStatsResource>(stats, 'e240s'),
      eMilestoneViewsPercentage: getAvg<OptimisationTestUnitStatsResource>(
        stats,
        'eMilestoneViewsPercentage',
      ),
      milestoneViewsPercentage: getAvg<OptimisationTestUnitStatsResource>(
        stats,
        'milestoneViewsPercentage',
      ),
      estimatedFinalValue: getAvg<OptimisationTestUnitStatsResource>(
        stats,
        'estimatedFinalValue',
      ),
      eytv4: getAvg<OptimisationTestUnitStatsResource>(stats, 'eytv4'),
      p25Rate: getAvg<OptimisationTestUnitStatsResource>(stats, 'p25Rate'),
      p50Rate: getAvg<OptimisationTestUnitStatsResource>(stats, 'p50Rate'),
      p75Rate: getAvg<OptimisationTestUnitStatsResource>(stats, 'p75Rate'),
      p100Rate: getAvg<OptimisationTestUnitStatsResource>(stats, 'p100Rate'),
      finalValue: getAvg<OptimisationTestUnitStatsResource>(
        stats,
        'finalValue',
      ),
    };
  }, [tests]);

  return (
    <Tr>
      <Td>AVG Metrics:</Td>
      <Td>{avgMetrics.views?.toFixed(0) || '-'}</Td>
      {tableViewOptions['Scene Duration'] && (
        <Td>
          {avgMetrics.sceneDuration
            ? durationFormatter(Math.round(avgMetrics.sceneDuration))
            : '-'}
        </Td>
      )}
      {tableViewOptions['CTR'] && (
        <Td>{avgMetrics.ctrPercentage?.toFixed(2) || '-'}</Td>
      )}
      {tableViewOptions['Watchtime (s)'] && (
        <Td>{avgMetrics.avgWatchtime?.toFixed(2) || '-'}</Td>
      )}
      {tableViewOptions['e30s Reach'] && (
        <Td>{avgMetrics.e30s?.toFixed(2) || '-'}</Td>
      )}
      {tableViewOptions['e45s Reach'] && (
        <Td>{avgMetrics.e45s?.toFixed(2) || '-'}</Td>
      )}
      {tableViewOptions['e60s Reach'] && (
        <Td>{avgMetrics.e60s?.toFixed(2) || '-'}</Td>
      )}
      {tableViewOptions['e80s Reach'] && (
        <Td>{avgMetrics.e80s?.toFixed(2) || '-'}</Td>
      )}
      {tableViewOptions['e120s Reach'] && (
        <Td>{avgMetrics.e120s?.toFixed(2) || '-'}</Td>
      )}
      {tableViewOptions['e150s Reach'] && (
        <Td>{avgMetrics.e150s?.toFixed(2) || '-'}</Td>
      )}
      {tableViewOptions['e4min Reach'] && (
        <Td>{avgMetrics.e240s?.toFixed(2) || '-'}</Td>
      )}
      {tableViewOptions['e8min Reach'] && (
        <Td>{avgMetrics.eMilestoneViewsPercentage?.toFixed(2) || '-'}</Td>
      )}
      {tableViewOptions['30s Reach'] && (
        <Td>{avgMetrics['30sViewsPercentage']?.toFixed(2) || '-'}</Td>
      )}
      {tableViewOptions['8min Reach'] && (
        <Td>{avgMetrics.milestoneViewsPercentage?.toFixed(2) || '-'}</Td>
      )}
      {tableViewOptions['25% Reach'] && (
        <Td>{avgMetrics.p25Rate?.toFixed(2) || '-'}</Td>
      )}
      {tableViewOptions['50% Reach'] && (
        <Td>{avgMetrics.p50Rate?.toFixed(2) || '-'}</Td>
      )}
      {tableViewOptions['75% Reach'] && (
        <Td>{avgMetrics.p75Rate?.toFixed(2) || '-'}</Td>
      )}
      {tableViewOptions['100% Reach'] && (
        <Td>{avgMetrics.p100Rate?.toFixed(2) || '-'}</Td>
      )}
      {tableViewOptions['Final Value'] && (
        <Td>{avgMetrics.finalValue?.toFixed(2) || '-'}</Td>
      )}
      {tableViewOptions['eYTV'] && (
        <Td>{avgMetrics.estimatedFinalValue?.toFixed(2) || '-'}</Td>
      )}
      {tableViewOptions['eYTV 4min'] && (
        <Td>{avgMetrics.eytv4?.toFixed(2) || '-'}</Td>
      )}
      {tableViewOptions['Channel'] && <Td />}
      {tableViewOptions['Audience Channels'] && <Td />}
      {tableViewOptions['Max CPV'] && <Td />}
      {tableViewOptions['Campaign Budget'] && <Td />}
      {tableViewOptions['Drop Visualization'] && <Td />}
      {tableViewOptions['Drop'] && <Td />}
      <Td />
    </Tr>
  );
};

export default AvgTipStatsRow;
